import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import heroTurnkey from '@/assets/hero-turnkey.jpg';
import heroProcess from '@/assets/hero-process.jpg';
import heroWaterFilling from '@/assets/hero-water-filling.jpg';
import heroSecondaryPackaging from '@/assets/hero-secondary-packaging.jpg';
import heroPalletizingRealistic from '@/assets/hero-palletizing-realistic.jpg';
import heroLab from '@/assets/hero-lab.jpg';
import heroTechnicalService from '@/assets/hero-technical-service.jpg';
import tftLogo from '@/assets/tft-logo.png';

const slides = [
  {
    title: { el: 'Ολοκληρωμένες Λύσεις', en: 'Turnkey Solutions' },
    description: { el: 'Από την ιδέα έως την παραγωγή — κάθε στάδιο, ένας συνεργάτης.', en: 'From idea to production — every stage, one partner.' },
    image: heroTurnkey
  },
  {
    title: { el: 'Εξοπλισμός Επεξεργασίας', en: 'Process Equipment' },
    description: { el: 'Καινοτόμες λύσεις επεξεργασίας που συνδυάζουν τεχνολογία, ασφάλεια και ενεργειακή απόδοση', en: 'Innovative processing solutions combining technology, safety and energy efficiency' },
    image: heroProcess
  },
  {
    title: { el: 'Πρωτογενής Συσκευασία', en: 'Primary Packaging' },
    description: { el: 'Πλήρωση και κλείσιμο προϊόντων με αξιοπιστία, ταχύτητα και ευελιξία.', en: 'Product filling and closing with reliability, speed and flexibility.' },
    image: heroWaterFilling
  },
  {
    title: { el: 'Δευτερογενής Συσκευασία', en: 'Secondary Packaging' },
    description: { el: 'Αυτοματοποιημένα συστήματα εγκιβωτισμού και ομαδοποίησης για μέγιστη αποδοτικότητα.', en: 'Automated boxing and grouping systems for maximum efficiency.' },
    image: heroSecondaryPackaging
  },
  {
    title: { el: 'Παλετοποίηση', en: 'Palletization' },
    description: { el: 'Ασφαλής και αποδοτική παλετοποίηση με ρομποτικούς χειρισμούς νέας γενιάς.', en: 'Safe and efficient palletizing with next-generation robotic handling.' },
    image: heroPalletizingRealistic
  },
  {
    title: { el: 'Εργαστηριακός Εξοπλισμός', en: 'Laboratory Equipment' },
    description: { el: 'Ακριβής ανάλυση και ποιοτικός έλεγχος — για απόλυτη ασφάλεια και ιχνηλασιμότητα προϊόντων.', en: 'Precise analysis and quality control — for absolute safety and product traceability.' },
    image: heroLab
  },
  {
    title: { el: 'Υπηρεσίες Υποστήριξης', en: 'After-Sales Services' },
    description: { el: 'Προληπτική συντήρηση, τεχνική υποστήριξη και ανταλλακτικά — για αδιάλειπτη παραγωγή.', en: 'Preventive maintenance, technical support and spare parts — for uninterrupted production.' },
    image: heroTechnicalService
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    if (!autoPlay) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [autoPlay]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setAutoPlay(false);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setAutoPlay(false);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative h-[70vh] bg-gradient-to-b from-background to-muted overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60" />
          <img
            src={slide.image}
            alt={slide.title[language]}
            className="h-full w-full object-cover"
            width="1920"
            height="1080"
            {...{ fetchpriority: index === 0 ? "high" : "low" }}
            loading={index === 0 ? "eager" : "lazy"}
          />
          <div className="absolute bottom-6 md:bottom-12 left-4 md:left-12 right-4 md:right-auto z-10">
            <div className="max-w-2xl text-white animate-fade-in">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4">{slide.title[language]}</h1>
              <p className="text-sm md:text-lg lg:text-xl mb-4 md:mb-8 text-white/90 line-clamp-2">{slide.description[language]}</p>
              <Button variant="default" size="lg" asChild className="border-2 border-white/30 hover:bg-white hover:text-primary transition-all">
                <Link to="/industries">{t('Κλάδοι Δραστηριότητας', 'Industries')}</Link>
              </Button>
            </div>
          </div>
          
          {/* Brand Statement - Centered */}
          <div className="absolute top-4 md:top-8 left-4 right-4 md:left-0 md:right-0 flex flex-col items-center justify-center text-center z-20">
            <div className="bg-black/30 backdrop-blur-sm rounded-lg md:rounded-2xl px-4 py-2 md:px-8 md:py-4">
              <h1 className="text-white text-base md:text-3xl lg:text-4xl font-bold drop-shadow-md leading-tight">
                Tesco Food Technology — Together for Tomorrow
              </h1>
              <p className="text-white text-xs md:text-sm lg:text-base mt-1 md:mt-2 tracking-wide uppercase opacity-90">
                Integration • Innovation • Integrity
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
        aria-label={t('Προηγούμενη διαφάνεια', 'Previous slide')}
      >
        <ChevronLeft className="h-6 w-6 text-white" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
        aria-label={t('Επόμενη διαφάνεια', 'Next slide')}
      >
        <ChevronRight className="h-6 w-6 text-white" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="p-2 group"
            aria-label={t(`Μετάβαση στη διαφάνεια ${index + 1}`, `Go to slide ${index + 1}`)}
          >
            <span className={`block h-2 rounded-full transition-all ${
              index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
            }`} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default Hero;